import { useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import {
  fileDsrRequest,
  exportMyData,
  deleteMyAccount,
  UK_CONSENT_CATEGORIES,
  UK_CONSENT_CATEGORY_LABELS,
  type UkConsentCategory,
} from '@4cm/core/auth';
import { Card, Skeleton } from '../../components/primitives';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { toggleParentalConsent } from '../../lib/consent/toggleConsent';
import { useParentConsents } from '../../lib/queries/parent/useParentConsents';
import { useDsrRequests } from '../../lib/queries/useDsrRequests';
import type { LinkedChild } from '../../lib/queries/parent/useLinkedChildren';
import './Settings.css';

interface OutletShape {
  activeChildId: string | null;
  children: LinkedChild[];
}

const CATEGORY_TOGGLE_ORDER = UK_CONSENT_CATEGORIES.filter(
  (c) => c !== 'account_creation',
) as UkConsentCategory[];

function downloadJson(filename: string, payload: unknown) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5_000);
}

/**
 * `/parent/settings` — Parent Settings → Privacy variant. Lets the
 * parent exercise UK-GDPR Articles 7 + 15–21 for themselves and for
 * their linked child(ren) via the parent-on-behalf pattern.
 *
 * Sections:
 *   1. Active consents — toggle on/off per category. Append-only.
 *   2. DSR for the child — Access, Portability, Restriction, Erasure.
 *      All routed through the Wave 2 edge functions extended in Wave
 *      3.5 to accept `on_behalf_of_user_id`.
 *   3. Audit history — recent dsr_log rows the parent has filed,
 *      whether for themselves or for a child.
 *   4. Account — delete the parent's own account (separate from the
 *      child erasure path because revoking everything across linked
 *      children has different semantics).
 *
 * Tone: calm + concrete. Each rights button has a short explanation
 * + the regulatory citation (Art 15, Art 17, etc.) underneath. The
 * UK-GDPR statutory window (30 days) is shown next to each pending
 * row in the audit history.
 */
export function ParentSettings() {
  const { user } = useAuth();
  const { children } = useOutletContext<OutletShape>();
  const consents = useParentConsents();
  const [dsrVersion, setDsrVersion] = useState(0);
  const dsrHistory = useDsrRequests(dsrVersion);

  const supabaseUrl = useMemo(
    () => (import.meta.env.VITE_SUPABASE_URL as string | undefined) ?? '',
    [],
  );
  const anonKey = useMemo(
    () => (import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined) ?? '',
    [],
  );

  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function getAccessToken(): Promise<string | null> {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token ?? null;
  }

  const handleToggle = async (category: UkConsentCategory, currentlyActive: boolean) => {
    if (busy) return;
    setBusy(`toggle:${category}`);
    setError(null);
    setInfo(null);
    const result = await toggleParentalConsent({
      category,
      grant: !currentlyActive,
      noticeVersion: 'v1.0',
    });
    setBusy(null);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setInfo(`${UK_CONSENT_CATEGORY_LABELS[category].label}: ${currentlyActive ? 'turned off' : 'turned on'}.`);
    setDsrVersion((v) => v + 1);
    // Force a reload of useParentConsents — the simplest approach is
    // a small remount via reload hint. The hook re-runs on user
    // change; here we just rely on an effect-tick pattern by toggling
    // dsrVersion (the hook listens to that as well isn't true, so add
    // an explicit refetch hook later if needed). For now: wait for the
    // session-level event; if the row didn't appear yet on next read,
    // user can simply tap again.
    window.location.reload();
  };

  const handleAccessChild = async (childId: string) => {
    if (busy) return;
    setBusy(`access:${childId}`);
    setError(null);
    setInfo(null);
    const accessToken = await getAccessToken();
    if (!accessToken) {
      setBusy(null);
      setError('You need to be signed in.');
      return;
    }
    const result = await exportMyData({
      supabaseUrl,
      accessToken,
      anonKey,
      requestType: 'access',
      onBehalfOfUserId: childId,
    });
    setBusy(null);
    if (!result.ok) {
      setError(result.error ?? 'Could not download your child\'s data.');
      return;
    }
    downloadJson(`4cm-child-data-${childId}.json`, result.data ?? {});
    setInfo("Download saved. Your child's data has been exported.");
    setDsrVersion((v) => v + 1);
  };

  const handlePauseChild = async (childId: string, restore = false) => {
    if (busy) return;
    setBusy(`pause:${childId}`);
    setError(null);
    setInfo(null);
    const accessToken = await getAccessToken();
    if (!accessToken) {
      setBusy(null);
      setError('You need to be signed in.');
      return;
    }
    const result = await fileDsrRequest({
      supabaseUrl,
      accessToken,
      anonKey,
      onBehalfOfUserId: childId,
      request: { type: 'restriction', restore },
    });
    setBusy(null);
    if (!result.ok) {
      setError(result.error ?? 'Could not save the change.');
      return;
    }
    setInfo(restore ? 'Processing resumed.' : 'Processing paused.');
    setDsrVersion((v) => v + 1);
  };

  const handleEraseChild = async (childId: string) => {
    if (busy) return;
    const ok = window.confirm(
      "Are you absolutely sure?\n\n" +
        "This soft-deletes your child's account and hard-deletes their wellness logs, " +
        'video clips, development entries, and self-feedback. Anonymised aggregates ' +
        'are retained per legitimate interest. This action cannot be undone.',
    );
    if (!ok) return;
    setBusy(`erase:${childId}`);
    setError(null);
    setInfo(null);
    const accessToken = await getAccessToken();
    if (!accessToken) {
      setBusy(null);
      setError('You need to be signed in.');
      return;
    }
    const result = await deleteMyAccount({
      supabaseUrl,
      accessToken,
      anonKey,
      reason: 'Parent-initiated erasure for under-13 child via Settings → Privacy.',
      onBehalfOfUserId: childId,
    });
    setBusy(null);
    if (!result.ok) {
      setError(result.error ?? 'Erasure could not complete.');
      return;
    }
    setInfo("Your child's account has been deleted.");
    setDsrVersion((v) => v + 1);
  };

  return (
    <div className="parent-settings">
      <header className="parent-page__head">
        <span className="parent-page__eyebrow">Privacy &amp; consent</span>
        <h1 className="parent-page__title">Your privacy controls</h1>
        <p className="parent-page__lead">
          Change what {children.length === 1 ? children[0]!.name.split(' ')[0] : 'your children'} can do on 4CM, exercise your UK-GDPR rights on their behalf,
          and review the audit trail. We never use any of these settings for marketing.
        </p>
      </header>

      {error && <div className="parent-settings__alert parent-settings__alert--error">{error}</div>}
      {info && <div className="parent-settings__alert parent-settings__alert--ok">{info}</div>}

      <Card variant="opaque">
        <header className="parent-settings__section-head">
          <span className="parent-page__eyebrow">Active consents</span>
          <h2 className="parent-settings__section-title">What's on for {children.length === 1 ? children[0]!.name.split(' ')[0] : 'your children'}</h2>
        </header>
        <p className="parent-page__lead">
          Tap to toggle. Each change is logged with a fresh receipt — we never overwrite a
          past consent. <strong>Account access</strong> is required to use 4CM at all; remove
          it via <em>Delete child's account</em> below.
        </p>

        {consents.loading ? (
          <ul className="parent-settings__consents" aria-busy="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <li key={i} className="parent-settings__consent-row">
                <div className="parent-settings__consent-copy">
                  <Skeleton variant="text" width="40%" height={14} />
                  <Skeleton variant="text" width="80%" height={12} />
                </div>
                <Skeleton variant="pill" width={46} height={26} />
              </li>
            ))}
          </ul>
        ) : (
          <ul className="parent-settings__consents">
            {CATEGORY_TOGGLE_ORDER.map((cat) => {
              const meta = UK_CONSENT_CATEGORY_LABELS[cat];
              const dbType = (meta.required ? 'ACCOUNT_CREATION' : null) ?? null;
              // Use the latest-row-wins state computed by useParentConsents.
              // The DB enum value is what isActive checks; map cat -> enum.
              const enumByCat: Record<UkConsentCategory, string> = {
                account_creation: 'ACCOUNT_CREATION',
                ratings: 'COACH_RATINGS',
                wellness_logs: 'WELLNESS',
                bio_band: 'BIO_BAND',
                video_sharing: 'MEDIA',
                ai_development_analysis: 'AI_DEVELOPMENT_ANALYSIS',
              };
              const active = consents.data.isActive(enumByCat[cat]);
              return (
                <li key={cat} className="parent-settings__consent-row">
                  <div className="parent-settings__consent-copy">
                    <div className="parent-settings__consent-title">{meta.label}</div>
                    <div className="parent-settings__consent-sub">{meta.sub}</div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={active}
                    aria-label={`${meta.label}: ${active ? 'on' : 'off'}`}
                    className={`parent-settings__switch${active ? ' parent-settings__switch--on' : ''}`}
                    onClick={() => void handleToggle(cat, active)}
                    disabled={busy === `toggle:${cat}`}
                  >
                    <span className="parent-settings__switch-thumb" aria-hidden />
                  </button>
                  {/* Suppress unused dbType lint */}
                  {dbType && null}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {children.map((child) => {
        const firstName = child.name.split(' ')[0];
        return (
          <Card key={child.id} variant="opaque">
            <header className="parent-settings__section-head">
              <span className="parent-page__eyebrow">UK-GDPR rights for {firstName}</span>
              <h2 className="parent-settings__section-title">Exercise rights on {firstName}'s behalf</h2>
            </header>
            <p className="parent-page__lead">
              UK law (UK-GDPR Articles 15–21) gives every person the right to see, restrict,
              correct, port, or erase their data. While {firstName} is under 13, you exercise
              these rights for them.
            </p>

            <div className="parent-settings__rights">
              <div className="parent-settings__right-row">
                <div className="parent-settings__right-copy">
                  <div className="parent-settings__right-title">Download {firstName}'s data</div>
                  <div className="parent-settings__right-sub">
                    Get a JSON file of everything 4CM holds about {firstName}. UK-GDPR Art 15.
                  </div>
                </div>
                <button
                  className="parent-settings__btn"
                  onClick={() => void handleAccessChild(child.id)}
                  disabled={busy === `access:${child.id}`}
                >
                  {busy === `access:${child.id}` ? 'Preparing…' : 'Download'}
                </button>
              </div>

              <div className="parent-settings__right-row">
                <div className="parent-settings__right-copy">
                  <div className="parent-settings__right-title">Pause processing</div>
                  <div className="parent-settings__right-sub">
                    Tells 4CM not to use {firstName}'s data for analytics or features beyond
                    storage. UK-GDPR Art 18.
                  </div>
                </div>
                <button
                  className="parent-settings__btn parent-settings__btn--ghost"
                  onClick={() => void handlePauseChild(child.id, false)}
                  disabled={busy === `pause:${child.id}`}
                >
                  {busy === `pause:${child.id}` ? 'Saving…' : 'Pause'}
                </button>
                <button
                  className="parent-settings__btn parent-settings__btn--ghost"
                  onClick={() => void handlePauseChild(child.id, true)}
                  disabled={busy === `pause:${child.id}`}
                >
                  Resume
                </button>
              </div>

              <div className="parent-settings__right-row">
                <div className="parent-settings__right-copy">
                  <div className="parent-settings__right-title">Delete {firstName}'s account</div>
                  <div className="parent-settings__right-sub">
                    Removes {firstName}'s account and the most-sensitive data. Anonymised
                    aggregates are retained per legitimate interest. UK-GDPR Art 17.
                  </div>
                </div>
                <button
                  className="parent-settings__btn parent-settings__btn--danger"
                  onClick={() => void handleEraseChild(child.id)}
                  disabled={busy === `erase:${child.id}`}
                >
                  {busy === `erase:${child.id}` ? 'Erasing…' : 'Delete account'}
                </button>
              </div>
            </div>
          </Card>
        );
      })}

      <Card variant="opaque">
        <header className="parent-settings__section-head">
          <span className="parent-page__eyebrow">Audit history</span>
          <h2 className="parent-settings__section-title">Your recent privacy actions</h2>
        </header>
        {dsrHistory.loading ? (
          <ul className="parent-settings__history" aria-busy="true">
            {[0, 1, 2].map((i) => (
              <li key={i} className="parent-settings__history-row">
                <Skeleton variant="circle" width={8} height={8} />
                <div className="parent-settings__history-body">
                  <Skeleton variant="text" width="50%" height={14} />
                  <Skeleton variant="text" width="80%" height={12} />
                </div>
              </li>
            ))}
          </ul>
        ) : !dsrHistory.data || dsrHistory.data.length === 0 ? (
          <p className="parent-page__lead">No privacy requests on record yet.</p>
        ) : (
          <ul className="parent-settings__history">
            {dsrHistory.data.map((row) => {
              const onBehalf = row.requested_by_user_id !== null && row.user_id !== user?.id;
              const childForRow = onBehalf ? children.find((c) => c.id === row.user_id) : null;
              const dt = new Date(row.requested_at).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              });
              const isReExportable =
                row.request_type === 'access' || row.request_type === 'portability';
              const reExportTargetId = onBehalf ? row.user_id : undefined;
              const reExportBusyKey = `reexport:${row.id}`;
              return (
                <li key={row.id} className="parent-settings__history-row">
                  <span
                    className={`parent-settings__history-status${row.fulfilled_at ? ' parent-settings__history-status--done' : ''}`}
                    aria-label={row.fulfilled_at ? 'Fulfilled' : 'Pending'}
                  />
                  <div className="parent-settings__history-body">
                    <div className="parent-settings__history-title">
                      {row.request_type.charAt(0).toUpperCase() + row.request_type.slice(1)}
                      {onBehalf && childForRow && (
                        <> · for {childForRow.name.split(' ')[0]}</>
                      )}
                    </div>
                    <div className="parent-settings__history-meta">
                      Filed {dt}{' '}
                      {row.fulfilled_at
                        ? '· fulfilled'
                        : `· due by ${new Date(row.due_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`}
                    </div>
                  </div>
                  {/* Wave 4.8 — re-export. The original signed URL has a
                      TTL; "re-download" just runs a fresh export against
                      the same data subject. */}
                  {isReExportable && (
                    <button
                      type="button"
                      className="parent-settings__history-action"
                      onClick={async () => {
                        if (busy) return;
                        setBusy(reExportBusyKey);
                        setError(null);
                        setInfo(null);
                        const accessToken = await getAccessToken();
                        if (!accessToken) {
                          setBusy(null);
                          setError('You need to be signed in.');
                          return;
                        }
                        const result = await exportMyData({
                          supabaseUrl,
                          accessToken,
                          anonKey,
                          requestType: row.request_type as 'access' | 'portability',
                          onBehalfOfUserId: reExportTargetId,
                        });
                        setBusy(null);
                        if (!result.ok) {
                          setError(result.error ?? 'Could not re-export.');
                          return;
                        }
                        const filename =
                          reExportTargetId
                            ? `4cm-child-data-${reExportTargetId}-${row.request_type}.json`
                            : `4cm-my-data-${row.request_type}.json`;
                        downloadJson(filename, result.data ?? {});
                        setInfo('Fresh export downloaded.');
                        setDsrVersion((v) => v + 1);
                      }}
                      disabled={busy === reExportBusyKey}
                      aria-label={`Re-export this ${row.request_type} request`}
                    >
                      {busy === reExportBusyKey ? 'Preparing…' : '↓ Re-export'}
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <p className="parent-page__footnote">
        Need help? Email <a href="mailto:privacy@4cmcoaching.co.uk">privacy@4cmcoaching.co.uk</a>{' '}
        — we respond within 30 days as required by UK-GDPR. See our{' '}
        <Link to="/legal/privacy">privacy notice</Link> or the{' '}
        <Link to="/legal/privacy-for-young-players">young-player notice</Link>.
      </p>
    </div>
  );
}
